import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const SpaceTechBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      
      // Use viewport dimensions for full screen coverage
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Set the actual size in memory (scaled to account for extra pixel density)
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      
      // Scale the drawing context so everything will work at the higher ratio
      ctx.scale(dpr, dpr);
      
      // Set the display size (css pixels)
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
    };

    // Space elements
    const stars = [];
    const shootingStars = [];
    const particles = [];

    // Create stars
    class Star {
      constructor() {
        this.x = Math.random() * window.innerWidth;
        this.y = Math.random() * window.innerHeight;
        this.size = Math.random() * 2 + 0.5;
        this.opacity = Math.random() * 0.8 + 0.2;
        this.twinkle = Math.random() * Math.PI * 2;
        this.twinkleSpeed = Math.random() * 0.02 + 0.01;
      }

      // Update position when canvas resizes
      updatePosition() {
        this.x = Math.random() * window.innerWidth;
        this.y = Math.random() * window.innerHeight;
      }

      update() {
        this.twinkle += this.twinkleSpeed;
      }

      draw() {
        ctx.save();
        const twinkleFactor = Math.sin(this.twinkle) * 0.3 + 0.7;
        ctx.globalAlpha = this.opacity * twinkleFactor;
        ctx.fillStyle = '#ffffff';
        ctx.shadowBlur = this.size * 2;
        ctx.shadowColor = '#ffffff';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    // Create shooting stars
    class ShootingStar {
      constructor() {
        this.x = Math.random() * window.innerWidth;
        this.y = Math.random() * window.innerHeight * 0.3;
        this.vx = (Math.random() - 0.5) * 8 + 2;
        this.vy = Math.random() * 2 + 1;
        this.size = Math.random() * 3 + 1;
        this.opacity = 1;
        this.trail = [];
        this.maxTrailLength = 20;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        
        // Add to trail
        this.trail.push({ x: this.x, y: this.y, opacity: this.opacity });
        if (this.trail.length > this.maxTrailLength) {
          this.trail.shift();
        }
        
        // Fade out
        this.opacity -= 0.02;
        
        // Reset if off screen or faded
        if (this.x > window.innerWidth || this.y > window.innerHeight || this.opacity <= 0) {
          this.x = Math.random() * window.innerWidth;
          this.y = Math.random() * window.innerHeight * 0.3;
          this.vx = (Math.random() - 0.5) * 8 + 2;
          this.vy = Math.random() * 2 + 1;
          this.opacity = 1;
          this.trail = [];
        }
      }

      draw() {
        ctx.save();
        
        // Draw trail
        for (let i = 0; i < this.trail.length; i++) {
          const point = this.trail[i];
          const alpha = (i / this.trail.length) * this.opacity;
          ctx.globalAlpha = alpha;
          ctx.fillStyle = '#ffffff';
          ctx.shadowBlur = 10;
          ctx.shadowColor = '#ffffff';
          ctx.beginPath();
          ctx.arc(point.x, point.y, this.size * (i / this.trail.length), 0, Math.PI * 2);
          ctx.fill();
        }
        
        // Draw main star
        ctx.globalAlpha = this.opacity;
        ctx.fillStyle = '#ffffff';
        ctx.shadowBlur = 15;
        ctx.shadowColor = '#ffffff';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.restore();
      }
    }


    // Create floating particles
    class Particle {
      constructor() {
        this.x = Math.random() * window.innerWidth;
        this.y = Math.random() * window.innerHeight;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.size = Math.random() * 1.5 + 0.5;
        this.opacity = Math.random() * 0.4 + 0.1;
        this.hue = Math.random() * 30 + 200; // Blue range
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        
        // Wrap around screen
        if (this.x < 0) this.x = window.innerWidth;
        if (this.x > window.innerWidth) this.x = 0;
        if (this.y < 0) this.y = window.innerHeight;
        if (this.y > window.innerHeight) this.y = 0;
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity;
        ctx.fillStyle = `hsl(${this.hue}, 70%, 60%)`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = `hsl(${this.hue}, 70%, 60%)`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    // Create tech particles
    class TechParticle {
      constructor() {
        this.x = Math.random() * window.innerWidth;
        this.y = Math.random() * window.innerHeight;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 1 + 0.5;
        this.opacity = Math.random() * 0.3 + 0.1;
        this.hue = Math.random() * 60 + 200; // Blue to cyan range
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        
        // Wrap around screen
        if (this.x < 0) this.x = window.innerWidth;
        if (this.x > window.innerWidth) this.x = 0;
        if (this.y < 0) this.y = window.innerHeight;
        if (this.y > window.innerHeight) this.y = 0;
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity;
        ctx.fillStyle = `hsl(${this.hue}, 80%, 70%)`;
        ctx.shadowBlur = 5;
        ctx.shadowColor = `hsl(${this.hue}, 80%, 70%)`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    // Initialize elements
    const initElements = () => {
      // Create stars
      for (let i = 0; i < 100; i++) {
        stars.push(new Star());
      }
      
      // Create shooting stars
      for (let i = 0; i < 3; i++) {
        shootingStars.push(new ShootingStar());
      }
      
      
      // Create tech particles
      for (let i = 0; i < 50; i++) {
        particles.push(new TechParticle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw all elements
      stars.forEach(star => {
        star.update();
        star.draw();
      });
      
      shootingStars.forEach(star => {
        star.update();
        star.draw();
      });
      
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });
      
      animationId = requestAnimationFrame(animate);
    };

    // Initialize
    const init = () => {
      // Ensure canvas is properly sized before creating elements
      setTimeout(() => {
        resizeCanvas();
        initElements();
        animate();
      }, 100);
    };

    // Handle mobile viewport changes
    const handleResize = () => {
      resizeCanvas();
      
      // Update existing star positions to fit new viewport
      stars.forEach(star => {
        star.updatePosition();
      });
      
      // Clear and recreate shooting stars and particles
      shootingStars.length = 0;
      particles.length = 0;
      
      // Create new particles for the new dimensions
      for (let i = 0; i < 50; i++) {
        particles.push(new Particle());
      }
      
      // Recreate shooting stars
      for (let i = 0; i < 3; i++) {
        shootingStars.push(new ShootingStar());
      }
    };

    // Initialize immediately
    init();

    // Add multiple event listeners for better mobile support
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      setTimeout(handleResize, 100);
    });
    
    // Handle mobile viewport changes
    if ('visualViewport' in window) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if ('visualViewport' in window) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  return (
    <motion.canvas
      ref={canvasRef}
      className="space-tech-background"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        objectFit: 'cover'
      }}
    />
  );
};

export default SpaceTechBackground;
